import PouchDB from "pouchdb-browser"
import { createNewMemory, review, type WordMemory, type Grade, shouldShow } from "./fsrs"
import type { WordMemoryDoc, ReviewLogDoc, LlemmaDoc } from "./db.types"

/** PouchDB database typed for Llemma documents. */
export type Db = PouchDB.Database<LlemmaDoc>

/** Create a PouchDB instance for browser use (IndexedDB). */
export function initDb(name = "llemma"): Db {
  return new PouchDB<LlemmaDoc>(name)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function wordId(lemma: string, lang: string): string {
  return `w:${lang}:${lemma}`
}

function reviewId(lemma: string, ts: number, lang: string): string {
  return `r:${lang}:${lemma}:${ts}`
}

function memoryFromDoc(doc: WordMemoryDoc): WordMemory {
  return {
    stability: doc.stability,
    difficulty: doc.difficulty,
    state: doc.state,
    lastReview: doc.lastReview,
    lapses: doc.lapses,
    reps: doc.reps,
  }
}

function wordDocFromMemory(lemma: string, mem: WordMemory, now: number, lang: string): WordMemoryDoc {
  return {
    _id: wordId(lemma, lang),
    type: "word",
    stability: mem.stability,
    difficulty: mem.difficulty,
    state: mem.state,
    lastReview: mem.lastReview,
    lapses: mem.lapses,
    reps: mem.reps,
    updatedAt: now,
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record a review and update the word memory doc.
 * Creates both a review log entry and an updated word memory doc.
 */
export async function reviewWord(
  db: Db,
  lemma: string,
  grade: Grade,
  now: number = Date.now(),
  lang: string = "es",
): Promise<WordMemory> {
  let existing: WordMemoryDoc | null = null
  try {
    existing = await db.get(wordId(lemma, lang)) as WordMemoryDoc
  } catch (e: any) {
    if (e.status !== 404) throw e
  }

  const currentMemory = existing ? memoryFromDoc(existing) : createNewMemory()
  const { memory: newMemory } = review(currentMemory, grade, now)

  const logDoc: ReviewLogDoc = {
    _id: reviewId(lemma, now, lang),
    type: "review",
    lemma,
    grade: grade as 1 | 2 | 3 | 4,
    timestamp: now,
  }

  const wordDoc = wordDocFromMemory(lemma, newMemory, now, lang)
  if (existing?._rev) {
    ;(wordDoc as any)._rev = existing._rev
  }

  await db.bulkDocs([logDoc as any, wordDoc as any])
  return newMemory
}

/**
 * Get the memory state for a single lemma, or null if never reviewed.
 */
export async function getWordMemory(
  db: Db,
  lemma: string,
  lang: string = "es",
): Promise<WordMemory | null> {
  try {
    const doc = await db.get(wordId(lemma, lang)) as WordMemoryDoc
    return memoryFromDoc(doc)
  } catch (e: any) {
    if (e.status === 404) return null
    throw e
  }
}

/**
 * Mark a lemma as permanently known — it will never appear in flashcards again.
 */
export async function markKnown(
  db: Db,
  lemma: string,
  lang: string = "es",
): Promise<void> {
  let existing: WordMemoryDoc | null = null
  try {
    existing = await db.get(wordId(lemma, lang)) as WordMemoryDoc
  } catch (e: any) {
    if (e.status !== 404) throw e
  }

  const doc: any = existing
    ? { ...existing, known: true, updatedAt: Date.now() }
    : { _id: wordId(lemma, lang), type: "word", stability: 0, difficulty: 0, state: 0, lastReview: 0, lapses: 0, reps: 0, updatedAt: Date.now(), known: true }

  await db.put(doc)
}

/**
 * From a list of lemmas, return those that need review (new or due).
 */
export async function getWordsNeedingReview(
  db: Db,
  lemmas: string[],
  threshold = 0.95,
  now: number = Date.now(),
  lang: string = "es",
): Promise<string[]> {
  if (lemmas.length === 0) return []

  const keys = lemmas.map(l => wordId(l, lang))
  const result = await db.allDocs({ keys, include_docs: true })

  const needReview: string[] = []
  for (let i = 0; i < result.rows.length; i++) {
    const row = result.rows[i]!
    const lemma = lemmas[i]!
    if ("error" in row || !row.doc) {
      needReview.push(lemma)
      continue
    }
    const doc = row.doc as unknown as WordMemoryDoc
    if (doc.known) continue
    const mem = memoryFromDoc(doc)
    if (shouldShow(mem, threshold, now)) {
      needReview.push(lemma)
    }
  }
  return needReview
}

// ---------------------------------------------------------------------------
// Conflict Resolution
// ---------------------------------------------------------------------------

async function replayLemma(db: Db, lemma: string, lang: string): Promise<WordMemory> {
  const logs = await db.allDocs({
    startkey: `r:${lang}:${lemma}:`,
    endkey: `r:${lang}:${lemma}:\uffff`,
    include_docs: true,
  })

  const reviews = logs.rows
    .filter(r => r.doc)
    .map(r => r.doc as unknown as ReviewLogDoc)
    .sort((a, b) => a.timestamp - b.timestamp || a._id.localeCompare(b._id))

  let mem = createNewMemory()
  for (const log of reviews) {
    const { memory } = review(mem, log.grade as Grade, log.timestamp)
    mem = memory
  }
  return mem
}

async function resolveConflict(db: Db, doc: WordMemoryDoc): Promise<void> {
  const conflicts = doc._conflicts
  if (!conflicts || conflicts.length === 0) return

  const parts = doc._id.split(":")  // "w:<lang>:<lemma>"
  const lang = parts[1]!
  const lemma = parts.slice(2).join(":")
  const mem = await replayLemma(db, lemma, lang)
  const now = Date.now()

  const resolved: any = {
    ...wordDocFromMemory(lemma, mem, now, lang),
    _rev: doc._rev,
  }

  const deletions = conflicts.map(rev => ({
    _id: doc._id,
    _rev: rev,
    _deleted: true,
  }))

  await db.bulkDocs([resolved, ...deletions])
}

export async function resolveAllConflicts(db: Db): Promise<void> {
  const result = await db.allDocs({
    startkey: "w:",
    endkey: "w:\uffff",
    include_docs: true,
    conflicts: true,
  })

  for (const row of result.rows) {
    const doc = row.doc as unknown as WordMemoryDoc
    if (doc._conflicts && doc._conflicts.length > 0) {
      await resolveConflict(db, doc)
    }
  }
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export function setupSync(
  db: Db,
  remoteUrl: string,
): PouchDB.Replication.Sync<{}> {
  const sync = db.sync(remoteUrl, {
    live: true,
    retry: true,
    batch_size: 100,
  })

  sync.on("paused", () => {
    resolveAllConflicts(db).catch(console.error)
  })

  return sync
}
